import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer/Footer';
import TelegramPopup from '../../Common/TelegramPopup/TelegramPopup';
import TokenCard from './TokenCard';
import LPPositionCard from './LPPositionCard';
import './TreasuryPage.scss';
import {
  BASE_DEXTOOLS_URL,
  BASE_LOCK_END_LABEL,
  BASE_LOCK_URL,
  BASE_PAIR_LABEL,
  BASE_POOL_CONTRACT_URL,
} from '../../../config/siteContent';
import {
  ASTROLESCENT_API_URL,
  BASE_LP_FALLBACK_SNAPSHOT,
  OCI_ENDOWMENT_FALLBACK_SNAPSHOT,
  RSWAP_GBOF_FALLBACK_SNAPSHOT,
  buildTreasuryTotals,
  fetchBasePairSnapshot,
  fetchOciEndowmentPoolSnapshot,
  fetchRswapGbofPoolSnapshot,
  getDerivedTreasuryDcksLiquidAmount,
  getOperationalTreasuryDcksAmount,
  getUnpairedTreasuryDcksAmount,
  OWNER_TREASURY_SNAPSHOT,
  SYMBOL_RESOURCE_MAP,
  calculateTreasuryTotalValueUSD,
} from './treasuryValue';

const formatCurrency = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '$0.00';
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatCompact = (num) => {
  if (typeof num !== 'number' || Number.isNaN(num)) return '0';
  if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(num >= 1e8 ? 1 : 2)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const tokenIconUrls = {
  REDDICKS:
    'https://ociswap.com/cdn-cgi/image/width=180,format=auto/https://blue-causal-gorilla-548.mypinata.cloud/ipfs/bafybeihxlt47xir73kc7ohfzs26ypls56on6jgcz4gnhzhrd7w34aqvk74',
  XRD:
    'https://ociswap.com/cdn-cgi/image/width=180,format=auto/https://assets.radixdlt.com/icons/icon-xrd-32x32.png',
  VBT:
    'https://ociswap.com/cdn-cgi/image/width=180,format=auto/https://vibolto.com/brand/vibolto-token.svg',
  MXRD:
    'https://ociswap.com/cdn-cgi/image/width=180,format=auto/https://ih0.redbubble.net/avatar.9379984.140x140.jpg',
  EARLY:
    'https://ociswap.com/cdn-cgi/image/width=180,format=auto/https://arweave.net/uXCQ9YVGkEijn7PS2wdkXqwkU_YrdgpNtQPH2Y1-Qcs',
  DELIVER:
    'https://ociswap.com/cdn-cgi/image/width=180,format=auto/https://arweave.net/EKJ6radHoK_j9yBjqkyBmNgo62ohmJIMGP2jyoYajB4',
  WOWO:
    'https://ociswap.com/cdn-cgi/image/width=180,format=auto/https://stakesafe.net/wowo/wowo.jpg',
  DFP2:
    'https://ociswap.com/cdn-cgi/image/width=180,format=auto/https://radix.defiplaza.net/assets/img/babylon/defiplaza-icon.png',
  ASTRL:
    'https://ociswap.com/cdn-cgi/image/width=180,format=auto/https://astrolescent.com/assets/img/tokens/astrl.png',
  OCI: 'https://ociswap.com/img/oci-logo.svg',
  LSU:
    'https://ociswap.com/cdn-cgi/image/width=180,format=auto/https://assets.radixdlt.com/icons/icon-xrd-32x32.png',
  GBOF:
    'https://api.caviarnine.com/v1.0/resource/image/resource_rdx1t40vqvcsljc2dhq9tfnjg98de056me5l676zeyf9694tztacnt2utu',
  ETH: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png',
  SOL: 'https://assets.coingecko.com/coins/images/4128/small/solana.png',
};

const buildLockedLPPositions = (basePairSnapshot, gbofPoolSnapshot) => [
  {
    id: 'treasury-locks',
    platform: 'Treasury Locks',
    platformIcon: tokenIconUrls.REDDICKS,
    lockType: 'timeLocked',
    tokens: [{ symbol: 'DCKS', amount: 125000000, iconUrl: tokenIconUrls.REDDICKS }],
    storedAt: 'Lock #417 + prior Radix locks',
    proofUrl: null,
    note: 'Lock #417 added another 50M DCKS for six months, bringing treasury-controlled Radix DCKS locks to 125M.',
  },
  {
    id: 'base-lp',
    platform: 'Base (Uniswap)',
    platformIcon: tokenIconUrls.ETH,
    lockType: 'timeLocked',
    tokens: [
      {
        symbol: 'cbETH',
        amount: basePairSnapshot.cbethAmount,
        iconUrl: tokenIconUrls.ETH,
      },
      {
        symbol: 'DCKS',
        amount: basePairSnapshot.dcksAmount,
        iconUrl: tokenIconUrls.REDDICKS,
      },
    ],
    storedAt: 'REDDICKS.ETH',
    proofUrl: BASE_LOCK_URL,
    proofLabel: 'Team Finance ->',
    note: `${BASE_PAIR_LABEL} is live and locked on Base. The current live pair snapshot shows roughly ${formatCompact(
      basePairSnapshot.dcksAmount
    )} DCKS and ${formatCompact(
      basePairSnapshot.cbethAmount
    )} cbETH in-pool, with Team Finance still showing the lock through ${BASE_LOCK_END_LABEL}.`,
  },
  {
    id: 'rswap-liquidity',
    platform: 'RSwap (Radix)',
    platformIcon: tokenIconUrls.REDDICKS,
    lockType: 'upcoming',
    tokens: [
      {
        symbol: 'GBOF',
        amount: gbofPoolSnapshot.gbofAmount,
        iconUrl: tokenIconUrls.GBOF,
      },
      {
        symbol: 'DCKS',
        amount: gbofPoolSnapshot.dcksAmount,
        iconUrl: tokenIconUrls.REDDICKS,
      },
    ],
    storedAt: 'RSwap GBOF / DCKS',
    proofUrl: gbofPoolSnapshot.poolUrl,
    proofLabel: 'Open Liquidity Board ->',
    note: `The former GBOF/XRD pool now sits on RSwap as a treasury-backed GBOF/DCKS position. The live board currently shows roughly ${formatCompact(
      gbofPoolSnapshot.gbofAmount
    )} GBOF and ${formatCompact(
      gbofPoolSnapshot.dcksAmount
    )} DCKS in-pool while formal Radix pool locks are still being finalized.`,
  },
  {
    id: 'sol-lp',
    platform: 'Solana (Raydium)',
    platformIcon: tokenIconUrls.SOL,
    lockType: 'upcoming',
    tokens: [
      { symbol: 'SOL', amount: 160, iconUrl: tokenIconUrls.SOL },
      { symbol: 'DCKS', amount: null, iconUrl: tokenIconUrls.REDDICKS },
    ],
    storedAt: 'REDDICKS.SOL',
    proofUrl: null,
    note: 'A liquid DCKS buffer is still being held back for Solana deployment. When that LP goes live, it is also expected to be locked.',
  },
];

const buildLiquidAssets = (basePairSnapshot, ociPoolSnapshot, gbofPoolSnapshot) => {
  const derivedDcksAmount = getDerivedTreasuryDcksLiquidAmount(
    basePairSnapshot,
    ociPoolSnapshot
  );
  const operationalDcksAmount = getOperationalTreasuryDcksAmount(
    basePairSnapshot,
    ociPoolSnapshot
  );
  const unpairedTreasuryDcksAmount = getUnpairedTreasuryDcksAmount(
    basePairSnapshot,
    ociPoolSnapshot,
    gbofPoolSnapshot
  );

  return [
    {
      name: 'Treasury DCKS Outside Locks',
      symbol: 'DCKS',
      amount: `~${Math.round(unpairedTreasuryDcksAmount).toLocaleString()}`,
      numericAmount: unpairedTreasuryDcksAmount,
      iconUrl: tokenIconUrls.REDDICKS,
      resourceAddress: SYMBOL_RESOURCE_MAP.DCKS,
      storedAt: 'REDDICKS.XRD',
      note: `The April 3 snapshot leaves about ${formatCompact(
        derivedDcksAmount
      )} DCKS outside hard locks. After removing the 1.2M DCKS endowment seed, about ${formatCompact(
        operationalDcksAmount
      )} remains in the treasury bucket. After accounting for the live ${formatCompact(
        gbofPoolSnapshot.dcksAmount
      )} DCKS already paired in the GBOF/DCKS RSwap pool, about ${formatCompact(
        unpairedTreasuryDcksAmount
      )} remains as the treasury reserve outside named locks and LPs.`,
    },
    {
      name: 'LSU Reserve',
      symbol: 'LSU',
      amount: `~${OWNER_TREASURY_SNAPSHOT.totalLsu.toLocaleString()}`,
      numericAmount: OWNER_TREASURY_SNAPSHOT.totalLsu,
      iconUrl: tokenIconUrls.LSU,
      resourceAddress: SYMBOL_RESOURCE_MAP.LSU,
      storedAt: 'REDDICKS.XRD',
      note: `Current updates put LSU inventory around ${formatCompact(
        OWNER_TREASURY_SNAPSHOT.totalLsu
      )}. It remains available for Radix liquidity and any final lock split updates as the next lock wave is added.`,
    },
    {
      name: 'Radix',
      symbol: 'XRD',
      amount: '1,000,000',
      numericAmount: 1000000,
      iconUrl: tokenIconUrls.XRD,
      resourceAddress: SYMBOL_RESOURCE_MAP.XRD,
      storedAt: 'REDDICKS.XRD',
      note: 'This is the remaining published treasury XRD balance outside the permanently locked Oci principal and outside the currently visible paired positions.',
    },
    {
      name: 'Vibolto',
      symbol: 'VBT',
      amount: '2,000,000',
      numericAmount: 2000000,
      iconUrl: tokenIconUrls.VBT,
      resourceAddress:
        'resource_rdx1t58j7rp8vwp397x6qsghfvakfp3n5znhyd8kz3gkssuej9j8cgshhz',
      storedAt: 'REDDICKS.XRD',
    },
    {
      name: 'Monstas',
      symbol: 'MXRD',
      amount: '57,150,000',
      numericAmount: 57150000,
      iconUrl: tokenIconUrls.MXRD,
      resourceAddress:
        'resource_rdx1th0pjp7s7fesstuj2hcggtxphzw3wc06qx5zrx0c6lsg0xalyt0nn0',
      storedAt: 'REDDICKS.XRD',
    },
    {
      name: 'DFP2',
      symbol: 'DFP2',
      amount: '403,848',
      numericAmount: 403848,
      iconUrl: tokenIconUrls.DFP2,
      resourceAddress:
        'resource_rdx1t5ywq4c6nd2lxkemkv4uzt8v7x7smjcguzq5sgafwtasa6luq7fclq',
      storedAt: 'REDDICKS.XRD',
    },
    {
      name: 'Wowo',
      symbol: 'WOWO',
      amount: '20,440,000',
      numericAmount: 20440000,
      iconUrl: tokenIconUrls.WOWO,
      resourceAddress:
        'resource_rdx1t4kc5ljyrwlxvg54s6gnctt7nwwgx89h9r2gvrpm369s23yhzyyzlx',
      storedAt: 'REDDICKS.XRD',
    },
    {
      name: 'Deliver',
      symbol: 'DELIVER',
      amount: '30,000,000',
      numericAmount: 30000000,
      iconUrl: tokenIconUrls.DELIVER,
      resourceAddress:
        'resource_rdx1t466mhd2l2jmmzxr8cg3mkwjqhs7zmjgtder2utnh0ue5msxrhyk3t',
      storedAt: 'REDDICKS.XRD',
    },
    {
      name: 'Early',
      symbol: 'EARLY',
      amount: '11,000,000',
      numericAmount: 11000000,
      iconUrl: tokenIconUrls.EARLY,
      resourceAddress:
        'resource_rdx1t5xv44c0u99z096q00mv74emwmxwjw26m98lwlzq6ddlpe9f5cuc7s',
      storedAt: 'REDDICKS.XRD',
    },
    {
      name: 'ASTRL',
      symbol: 'ASTRL',
      amount: '21,149',
      numericAmount: 21149,
      iconUrl: tokenIconUrls.ASTRL,
      resourceAddress:
        'resource_rdx1t4tjx4g3qzd98nayqxm7qdpj0a0u8ns6a0jrchq49dyfevgh6u0gj3',
      storedAt: 'REDDICKS.XRD',
    },
  ];
};

const SummaryStatCard = ({ label, value, icon, accent, approximate = false }) => (
  <div className={`summary-stat-card ${accent || ''}`}>
    <span className="summary-stat-icon">{icon}</span>
    <div className="summary-stat-content">
      <span className="summary-stat-value">
        {approximate ? `~${formatCompact(value)}` : formatCompact(value)}
      </span>
      <span className="summary-stat-label">{label}</span>
    </div>
  </div>
);

const TreasuryPage = () => {
  const [totalValueUSD, setTotalValueUSD] = useState(0);
  const [loadingTotalValue, setLoadingTotalValue] = useState(true);
  const [errorTotalValue, setErrorTotalValue] = useState(null);
  const [allApiTokenData, setAllApiTokenData] = useState(null);
  const [basePairSnapshot, setBasePairSnapshot] = useState(BASE_LP_FALLBACK_SNAPSHOT);
  const [ociPoolSnapshot, setOciPoolSnapshot] = useState(OCI_ENDOWMENT_FALLBACK_SNAPSHOT);
  const [gbofPoolSnapshot, setGbofPoolSnapshot] = useState(RSWAP_GBOF_FALLBACK_SNAPSHOT);
  const explanationRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  const treasuryTotals = useMemo(
    () => buildTreasuryTotals(basePairSnapshot, ociPoolSnapshot),
    [basePairSnapshot, ociPoolSnapshot]
  );
  const lockedLPPositions = useMemo(
    () => buildLockedLPPositions(basePairSnapshot, gbofPoolSnapshot),
    [basePairSnapshot, gbofPoolSnapshot]
  );
  const liquidAssets = useMemo(
    () => buildLiquidAssets(basePairSnapshot, ociPoolSnapshot, gbofPoolSnapshot),
    [basePairSnapshot, ociPoolSnapshot, gbofPoolSnapshot]
  );

  useEffect(() => {
    document.body.style.overflow = 'auto';

    if (location.hash) {
      const el = document.getElementById(location.hash.substring(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    }

    const fetchPrices = async () => {
      setLoadingTotalValue(true);
      setErrorTotalValue(null);

      let liveBaseSnapshot = BASE_LP_FALLBACK_SNAPSHOT;
      let liveOciSnapshot = OCI_ENDOWMENT_FALLBACK_SNAPSHOT;
      let liveGbofSnapshot = RSWAP_GBOF_FALLBACK_SNAPSHOT;

      const [baseResult, ociResult, gbofResult] = await Promise.allSettled([
        fetchBasePairSnapshot(),
        fetchOciEndowmentPoolSnapshot(),
        fetchRswapGbofPoolSnapshot(),
      ]);

      if (baseResult.status === 'fulfilled') {
        liveBaseSnapshot = baseResult.value;
      } else {
        console.warn('Failed to fetch live Base pair snapshot:', baseResult.reason);
      }

      if (ociResult.status === 'fulfilled') {
        liveOciSnapshot = ociResult.value;
      } else {
        console.warn('Failed to fetch live Oci endowment snapshot:', ociResult.reason);
      }

      if (gbofResult.status === 'fulfilled') {
        liveGbofSnapshot = gbofResult.value;
      } else {
        console.warn('Failed to fetch live RSwap GBOF pool snapshot:', gbofResult.reason);
      }

      setBasePairSnapshot(liveBaseSnapshot);
      setOciPoolSnapshot(liveOciSnapshot);
      setGbofPoolSnapshot(liveGbofSnapshot);

      try {
        const res = await fetch(ASTROLESCENT_API_URL);
        if (!res.ok) throw new Error(`Status: ${res.status}`);
        const priceMap = await res.json();
        setAllApiTokenData(priceMap);
        setTotalValueUSD(
          calculateTreasuryTotalValueUSD(
            priceMap,
            liveBaseSnapshot,
            liveOciSnapshot,
            liveGbofSnapshot
          )
        );
      } catch (err) {
        console.error('Error fetching treasury value:', err);
        setErrorTotalValue(err.message || 'Failed to calculate treasury value.');
      } finally {
        setLoadingTotalValue(false);
      }
    };

    fetchPrices();

    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        }),
      { threshold: 0.1 }
    );
    const node = explanationRef.current;
    if (node) observer.observe(node);

    return () => {
      document.body.style.overflow = '';
      if (node) observer.unobserve(node);
      observer.disconnect();
    };
  }, [location]);

  return (
    <div className="treasury-page" data-scroll-root>
      <Header />
      <main className="treasury-content">
        <section className="treasury-hero-section">
          <div className="container">
            <h1>Inside the Reddicks Vault</h1>
            <p className="subtitle">
              A multi-chain treasury powering Base liquidity, Radix treasury locks,
              active RSwap deployment, and future Solana expansion. The permanently
              locked Oci principal now sits under the endowment, so this page tracks
              treasury-only positions.
            </p>

            <div className="treasury-snapshot-banner">
              <span className="treasury-snapshot-banner__kicker">
                {OWNER_TREASURY_SNAPSHOT.dateLabel} snapshot
              </span>
              <p>
                The April 3 update puts 125M DCKS in treasury locks on
                Radix, about 15M DCKS in the locked Base LP, and the active
                GBOF/DCKS pool on RSwap. The permanently locked Oci pool plus
                the 1.2M DCKS seed are now tracked on the charity page as the
                current endowment.
              </p>
            </div>

            <div className="treasury-total-value-card">
              <div className="shine" />
              <h4>Total Treasury Value</h4>
              {loadingTotalValue && <p className="loading-message">Loading value...</p>}
              {errorTotalValue && <p className="error-message">Error: {errorTotalValue}</p>}
              {!loadingTotalValue && !errorTotalValue && (
                <div className="total-value">{formatCurrency(totalValueUSD)}</div>
              )}
              <p className="data-source">
                Powered by{' '}
                <a href="https://astrl.trade/" target="_blank" rel="noopener noreferrer">
                  Astrolescent API
                </a>{' '}
                plus live snapshots from{' '}
                <a
                  href={basePairSnapshot.pairUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Dexscreener
                </a>
                ,{' '}
                <a
                  href={ociPoolSnapshot.poolUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ociswap
                </a>
                , and{' '}
                <a
                  href={gbofPoolSnapshot.poolUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  RSwap
                </a>
              </p>
            </div>
          </div>
        </section>

        <section className="treasury-totals-section" id="totals">
          <div className="container">
            <h2 className="section-title">Treasury Totals</h2>
            <p className="totals-subtitle">
              Treasury totals now exclude the permanently locked Oci endowment
              principal and the separate 1.2M DCKS seed. They cover Radix
              treasury locks, the live Base LP, the current RSwap GBOF/DCKS
              position, and the remaining treasury reserve shared in the{' '}
              {OWNER_TREASURY_SNAPSHOT.dateLabel}.
            </p>
            <div className="summary-stats-grid">
              <SummaryStatCard
                label="Treasury DCKS"
                value={treasuryTotals.DCKS}
                icon="DCKS"
                accent="accent-dcks"
                approximate
              />
              <SummaryStatCard
                label="Treasury-Locked DCKS"
                value={treasuryTotals.LOCKED_DCKS}
                icon="LOCK"
                accent="accent-dcks"
                approximate
              />
              <SummaryStatCard
                label="Treasury XRD"
                value={treasuryTotals.XRD}
                icon="XRD"
                accent="accent-xrd"
              />
              <SummaryStatCard
                label="Total LSU"
                value={treasuryTotals.LSU}
                icon="LSU"
                accent="accent-lsu"
                approximate
              />
              <SummaryStatCard
                label="Total cbETH"
                value={treasuryTotals.ETH}
                icon="ETH"
                accent="accent-eth"
              />
              <SummaryStatCard
                label="Total SOL"
                value={treasuryTotals.SOL}
                icon="SOL"
                accent="accent-sol"
              />
            </div>
          </div>
        </section>

        <section className="locked-lp-section" id="locked-lp">
          <div className="container">
            <h2 className="section-title">Treasury Locks and LPs</h2>
            <p className="section-subtitle">
              Current treasury locks, live LP positions, and active deployment
              inventory. The permanently locked Oci principal now lives on the
              endowment page, so the cards below are treasury-only positions.
            </p>

            <div className="lp-positions-grid">
              {lockedLPPositions.map((lp, i) => {
                const tokenPrices = {};
                lp.tokens.forEach((token) => {
                  const radixAddr = SYMBOL_RESOURCE_MAP[token.symbol];
                  if (radixAddr && allApiTokenData) {
                    tokenPrices[token.symbol] =
                      allApiTokenData[radixAddr]?.tokenPriceUSD || 0;
                  }
                });

                return (
                  <LPPositionCard
                    key={lp.id}
                    {...lp}
                    animDelay={i * 80}
                    tokenPrices={tokenPrices}
                    isLoadingPrice={loadingTotalValue}
                  />
                );
              })}
            </div>

          </div>
        </section>

        <section className="liquid-assets-section" id="liquid-assets">
          <div className="container">
            <h2 className="section-title">Treasury Holdings</h2>
            <p className="section-subtitle">
              Treasury balances outside permanent locks and named LP positions.
              The permanently locked Oci principal and the 1.2M DCKS seed are
              tracked separately on the charity page as the current endowment.
            </p>
            <div className="token-holdings-grid">
              {liquidAssets.map((token, i) => {
                const info =
                  allApiTokenData && token.resourceAddress
                    ? allApiTokenData[token.resourceAddress]
                    : null;
                return (
                  <TokenCard
                    key={i}
                    iconUrl={token.iconUrl}
                    name={token.name}
                    symbol={token.symbol}
                    amount={token.amount}
                    numericAmount={token.numericAmount}
                    tokenPriceUSD={info?.tokenPriceUSD ?? null}
                    isLoadingPrice={loadingTotalValue}
                    storedAt={token.storedAt}
                    note={token.note}
                  />
                );
              })}
            </div>
          </div>
        </section>

        <section className="treasury-explanation-section" ref={explanationRef}>
          <div className="container">
            <h2 className={`section-title fade-in-title ${isVisible ? 'visible' : ''}`}>
              What is the Reddicks Treasury?
            </h2>

            <div className="explanation-cards-grid">
              <div
                className={`explanation-card fade-in-text ${isVisible ? 'visible' : ''}`}
                style={{ transitionDelay: '0.15s' }}
              >
                <div className="explanation-card-icon">Treasury</div>
                <h3>Community Resource Hub</h3>
                <p>
                  The Reddicks Treasury is the community resource hub used to fund
                  protocol-owned liquidity, support ecosystem products, and keep
                  strategic inventory available across Radix, Base, and later
                  Solana under DAO control.
                </p>
              </div>

              <div
                className={`explanation-card fade-in-text ${isVisible ? 'visible' : ''}`}
                style={{ transitionDelay: '0.3s' }}
              >
                <div className="explanation-card-icon">Cross-chain</div>
                <h3>Cross-Chain Vision</h3>
                <p>
                  Base still handles first impressions with the public lock and
                  explorer stack, while Radix now carries the treasury lock stack,
                  the main RSwap liquidity footprint, and the next wave of lock
                  expansion.
                </p>
              </div>

              <div
                className={`explanation-card fade-in-text ${isVisible ? 'visible' : ''}`}
                style={{ transitionDelay: '0.45s' }}
              >
                <div className="explanation-card-icon">Endowment</div>
                <h3>Separate Endowment Track</h3>
                <p>
                  The endowment now sits outside the treasury view. The charity
                  page tracks the permanently locked Oci principal, the separate
                  1.2M DCKS seed, and the fee-driven donation model built on top
                  of that principal.
                </p>
              </div>
            </div>

            <div
              className={`explanation-cta fade-in-text ${isVisible ? 'visible' : ''}`}
              style={{ transitionDelay: '0.6s' }}
            >
              <p>
                This treasury is more than a balance sheet. It funds liquidity,
                operations, and the long-term endowment direction behind REDDICKS.
              </p>
              <Link to="/verify" className="learn-more-link animated-link">
                Verify Current Trust Signals -&gt;
              </Link>
            </div>
          </div>
        </section>

        <section className="treasury-charity-cta-section">
          <div className="container">
            <div className="treasury-charity-cta-card">
              <span className="treasury-charity-cta-card__kicker">Next Destination</span>
              <h2 className="section-title">Treasury Supports the Endowment Story</h2>
              <p>
                The treasury now tracks execution and deployable reserves. The
                charity page tracks the locked Oci principal, the 1.2M DCKS seed,
                and how yearly fees are intended to turn into donations.
              </p>
              <div className="treasury-charity-cta-card__proof">
                <a href={BASE_LOCK_URL} target="_blank" rel="noopener noreferrer">
                  Team Finance
                </a>
                <a href={BASE_DEXTOOLS_URL} target="_blank" rel="noopener noreferrer">
                  DEXTools
                </a>
                <a
                  href={BASE_POOL_CONTRACT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Base Pool
                </a>
              </div>
              <Link to="/charity" className="treasury-charity-cta-card__button">
                Go to Charity
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <TelegramPopup />
    </div>
  );
};

export default TreasuryPage;
